<template>
  <!-- Contenedor -->
  <v-container fluid>
    <!-- Fila si la venta está cargando -->
    <v-row v-if="isLoading" no-gutters class="text-center">
      <v-col cols="12" class="spinner">
        <v-progress-circular size="50" indeterminate color="primary" />
      </v-col>
    </v-row>
    <!-- Fila para mostrar el contenido -->
    <v-row v-else no-gutters>
      <!-- Columna en caso de error -->
      <v-col
        v-if="isThereAnError"
        class="custom-height"
        align="center"
        justify="center"
      >
        <v-row class="fill-height" align="center" justify="center">
          <!-- Columna para la imagen -->
          <v-col cols="12" align="center" justify="center">
            <v-img
              class="hidden-md-and-up"
              src="https://deltia-production.s3.amazonaws.com/static/ghost.svg"
              width="30%"
              alt="Fantasma triste"
            />
            <v-img
              class="hidden-sm-and-down"
              src="https://deltia-production.s3.amazonaws.com/static/ghost.svg"
              width="20%"
              alt="Fantasma triste"
            />
          </v-col>
          <!-- Columna para el mensaje de error -->
          <v-col cols="12" align="center" justify="center">
            <h3 class="font-weight-bold red--text test--darken-2">
              {{ errorMessage }}
            </h3>
          </v-col>
        </v-row>
      </v-col>
      <!-- Columna para el contenido de la venta -->
      <v-col v-else cols="12">
        <DeltiaCard v-if="!isThereAnError" no-gutters>
          <!-- Total de la venta -->
          <v-col cols="6">
            <p class="font-weight-medium text--disabled mb-0">
              Total
            </p>
            <h1 class="teal--text text--accent-4">${{ sale.total }}</h1>
          </v-col>
          <!-- Estado de la venta -->
          <v-col cols="6" class="text-right">
            <p class="font-weight-medium text--disabled mb-0">
              Estado
            </p>
            <v-btn
              :color="getSaleStatusData(sale.status).color"
              class="white--text"
              depressed
              rounded
            >
              {{ getSaleStatusData(sale.status).name }}
            </v-btn>
          </v-col>
          <!-- Cantidad adicional -->
          <v-col cols="12" v-if="sale.additional_amount > 0">
            <p class="font-weight-medium text--disabled mb-0">
              Monto adicional
            </p>
            <p class="text-h6 mb-0 primary--text font-weight-medium">
              ${{ sale.additional_amount }}
            </p>
          </v-col>
          <!-- Fecha de la venta -->
          <v-col cols="12">
            <p class="font-weight-medium text--disabled mb-0">
              Fecha
            </p>
            <h3 class="content--text">
              {{ formatDate(sale.created_at) }}
            </h3>
          </v-col>
          <!-- Línea divisora -->
          <v-col cols="12" md="12" class="mt-2 mb-3">
            <v-divider></v-divider>
          </v-col>
          <!-- Fila para los encabezados de la venta -->
          <v-col cols="2" md="1">
            <p class="font-weight-medium text--disabled mb-0">Imagen</p>
          </v-col>
          <v-col cols="5" md="3">
            <p
              class="hidden-sm-and-down font-weight-medium text--disabled mb-0"
            >
              Nombre
            </p>
            <p class="hidden-md-and-up font-weight-medium text--disabled mb-0">
              Información
            </p>
          </v-col>
          <v-col cols="3" md="2" class="hidden-sm-and-down text-center">
            <p class="font-weight-medium text--disabled mb-0">
              Precio unitario
            </p>
          </v-col>
          <v-col cols="2" md="2" class="hidden-sm-and-down text-center">
            <p class="font-weight-medium text--disabled mb-0">Cantidad</p>
          </v-col>
          <v-col cols="3" md="2" class="text-center">
            <p class="font-weight-medium text--disabled mb-0">Descuento</p>
          </v-col>
          <v-col cols="2" md="2" class="text-right">
            <p class="font-weight-medium text--disabled mb-0">Subtotal</p>
          </v-col>
          <!-- Columna para los productos -->
          <v-col cols="12">
            <!-- Productos de la venta -->
            <v-row
              v-for="product in sale.products"
              :key="product._id"
              class="mb-5"
              align="center"
              no-gutters
            >
              <!-- Imagen del producto -->
              <v-col cols="2" md="1" class="text-center">
                <v-img
                  class="DeltiaHitProductImage"
                  aspect-ratio="1"
                  :src="product.image_url"
                  height="60px"
                  width="60px"
                />
              </v-col>
              <!-- Nombre del producto -->
              <v-col cols="5" md="3">
                <span class="hidden-sm-and-down content--text font-weight-bold">
                  {{ product.name }}
                </span>
                <span class="hidden-md-and-up content--text font-weight-bold">
                  {{ product.name }} (${{ product.price_per_unit }}x{{
                    product.quantity
                  }})
                </span>
              </v-col>
              <!-- Precio unitario -->
              <v-col cols="3" md="2" class="hidden-sm-and-down text-center">
                <span class="content--text font-weight-medium">
                  ${{ product.price_per_unit }}
                </span>
              </v-col>
              <!-- Cantidad -->
              <v-col cols="2" md="2" class="hidden-sm-and-down text-center">
                <span class="content--text font-weight-medium">
                  {{ product.quantity }}
                </span>
              </v-col>
              <!-- Descuento -->
              <v-col cols="3" md="2" class="text-center">
                <span class="content--text font-weight-medium">
                  ${{ product.discount }}
                </span>
              </v-col>
              <!-- Subtotal -->
              <v-col cols="2" md="2" class="text-right">
                <span class="content--text font-weight-medium">
                  ${{ product.amount }}
                </span>
              </v-col>
            </v-row>
          </v-col>
        </DeltiaCard>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Importar componentes
const DeltiaCard = () => import("@/components/DeltiaCard");

// Importar mixins
import dateFormat from "@/mixins/dateFormat.js";
import salesStatuses from "@/mixins/salesStatuses.js";

// Importar servicios
import MovementsServices from "@/services/MovementsServices";

export default {
  name: "SharedSale",
  mixins: [dateFormat, salesStatuses],
  components: {
    DeltiaCard
  },
  data() {
    return {
      isLoading: true,
      isThereAnError: false,
      sale: {}
    };
  },
  mounted: async function() {
    try {
      // Obtener id de la venta
      const { saleId } = this.$route.query;

      // Obtener la venta
      this.sale = await MovementsServices.getShareableSale(saleId);

      // Ocultar spinner
      this.isLoading = false;
    } catch (err) {
      // Ocultar spinner
      this.isLoading = false;

      // Mostrar error
      this.showError(err.message);
    }
  },
  methods: {
    showError: function(errorMessage) {
      this.isThereAnError = true;
      this.errorMessage = errorMessage;
    }
  }
};
</script>

<style scoped>
.spinner {
  height: 80vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-height {
  height: 90vh !important;
}

.DeltiaHitProductImage {
  border-radius: 8px;
}
</style>
